// src/components/ClimateAnalysisPanel.jsx
// Climate Analysis is a menu of sub-analyses. "Sun Path", "Wind Path" and
// "Temperature Analysis" are live; the rest are placeholders. Selecting a
// tool opens it with a Back affordance.
import React, { useState } from 'react';
import SunPathPanel from './SunPathPanel';
import WindPanel from './WindPanel';
import TemperaturePanel from './TemperaturePanel';
import UtilitiesPanel from './UtilitiesPanel';
import { API_BASE } from '../utils/apiBase';
import { CLIMATE_TOOL_ICON } from './AnalysisTabIcons';

// same backend origin the other panels use. TODO(centralise): utils/apiBase.js
const TOOLS = [
  { key: 'sun',   label: 'Sun Path',                desc: 'Ray impact & obstruction · per hour / month',        ready: true },
  { key: 'wind',  label: 'Wind Path',               desc: 'Prevailing wind · static / dynamic',                 ready: true },
  { key: 'temperature', label: 'Temperature Analysis', desc: 'Solar gain, HVAC & window sizing, materials',     ready: true },
  { key: 'water', label: 'Water Consumption',       desc: 'Demand from massing · SDBR/NBC per-capita norms',    ready: true },
  { key: 'power', label: 'Electricity Consumption', desc: 'Load, energy & statutory solar requirement',          ready: true },
];

export default function ClimateAnalysisPanel({ project }) {
  const [tool, setTool] = useState(null);

  const backBar = (title) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
      <button
        onClick={() => setTool(null)}
        style={{
          fontSize: 12, cursor: 'pointer', padding: '5px 9px', borderRadius: 8,
          border: '0.5px solid var(--border,#3a4456)', background: 'var(--surface-1,#141a28)',
          color: 'var(--text-secondary,#c3c9d4)',
        }}
      >
        &lsaquo; Climate
      </button>
      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary,#e5e7eb)' }}>{title}</span>
    </div>
  );

  if (tool === 'sun') {
    return (
      <div style={{ fontFamily: 'inherit' }}>
        {backBar('Sun Path')}
        <SunPathPanel project={project} />
      </div>
    );
  }

  if (tool === 'wind') {
    return (
      <div style={{ fontFamily: 'inherit' }}>
        {backBar('Wind Path')}
        <WindPanel project={project} />
      </div>
    );
  }

  if (tool === 'water') {
    return (
      <div style={{ fontFamily: 'inherit' }}>
        {backBar('Water Consumption')}
        <UtilitiesPanel project={project} apiBase={API_BASE} kind="water" />
      </div>
    );
  }

  if (tool === 'power') {
    return (
      <div style={{ fontFamily: 'inherit' }}>
        {backBar('Electricity Consumption')}
        <UtilitiesPanel project={project} apiBase={API_BASE} kind="electricity" />
      </div>
    );
  }

  if (tool === 'temperature') {
    return (
      <div style={{ fontFamily: 'inherit' }}>
        {backBar('Temperature Analysis')}
        <TemperaturePanel project={project} />
      </div>
    );
  }

  // ---- menu ----
  return (
    <div style={{ fontFamily: 'inherit' }}>
      <p style={{ fontSize: 12, color: 'var(--text-secondary,#c3c9d4)', margin: '0 2px 12px' }}>
        Choose an analysis to run against the current massing.
      </p>

      {TOOLS.map((t) => {
        const Icon = CLIMATE_TOOL_ICON[t.key];
        return (
        <div
          key={t.key}
          className={`climate-tool-card${t.ready ? ' is-ready' : ''}`}
          onClick={() => t.ready && setTool(t.key)}
          role="button"
          tabIndex={t.ready ? 0 : -1}
          onKeyDown={(e) => t.ready && (e.key === 'Enter' || e.key === ' ') && setTool(t.key)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
            padding: '12px 14px', marginBottom: 8, borderRadius: 12,
            border: `0.5px solid ${t.ready ? 'var(--border-accent,#378add)' : 'var(--border,#3a4456)'}`,
            background: 'var(--surface-2,#1a2234)',
            cursor: t.ready ? 'pointer' : 'default',
            opacity: t.ready ? 1 : 0.55,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            {/* The icon is decoration beside the name, which stays the label. Drawn in
                --accent on a faint tint of it: 4.7:1 dark, 4.0:1 light against the tile,
                over the 3:1 a graphic needs. --btn-active was 2.6:1 in the dark theme —
                it is dark on purpose, to carry white text. */}
            {Icon && (
              <span
                aria-hidden="true"
                style={{
                  flex: '0 0 auto', width: 34, height: 34, borderRadius: 9,
                  display: 'grid', placeItems: 'center', fontSize: 18,
                  color: 'var(--accent,#5da5e5)',
                  background: 'color-mix(in srgb, var(--accent,#5da5e5) 12%, var(--surface-1,#20262f))',
                  border: '0.5px solid color-mix(in srgb, var(--accent,#5da5e5) 35%, transparent)',
                }}
              >
                <Icon />
              </span>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary,#e5e7eb)' }}>{t.label}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted,#8b92a0)', marginTop: 2 }}>{t.desc}</div>
            </div>
          </div>
          {t.ready ? (
            <span style={{ fontSize: 16, color: 'var(--text-accent,#7fb4f0)' }}>&rsaquo;</span>
          ) : (
            <span style={{
              fontSize: 10, textTransform: 'uppercase', letterSpacing: '.5px',
              color: 'var(--text-muted,#8b92a0)', border: '0.5px solid var(--border,#3a4456)',
              borderRadius: 5, padding: '2px 6px',
            }}>Soon</span>
          )}
        </div>
        );
      })}
    </div>
  );
}
